import { useNavigate } from 'react-router-dom';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import Pill from './ui/Pill';
import { dateShort, DASH } from '../lib/format';

interface Person {
  id: string;
  name: string;
  email: string;
}

export interface MentorshipDetail {
  seasons: {
    seasonId: number;
    seasonName: string;
    seasonIsActive: boolean;
    role: string;
    isActive: boolean;
    sessionsScheduled: number;
    sessionsAttended: number;
  }[];
  pairings?: { seasonId: number; role: string; with: Person | null }[];
  sessions?: {
    seasonId: number;
    scheduledAt: string | null;
    role: string;
    with: Person | null;
    attended: boolean;
    feedbackSubmitted: boolean;
  }[];
  appProfile?: {
    title: string | null;
    bio: string | null;
    bioFr: string | null;
    supervisor: string | null;
    phone: string | null;
    pictureUrl: string | null;
    dataDate: string | null;
  } | null;
  staleNotes?: { field: string; app: string; platform: string }[];
}

const FIELD_LABEL: Record<string, string> = {
  organization: 'Institution',
  discipline: 'Discipline',
  careerSeniority: 'Career stage',
};

export default function MemberMentorship({ detail }: { detail: MentorshipDetail }) {
  const navigate = useNavigate();
  const open = (p: Person | null) => p && navigate(`/analytics/members/${p.id}`);
  const seasons = [...detail.seasons].sort((a, b) => b.seasonId - a.seasonId);
  const app = detail.appProfile;
  const now = Date.now();

  return (
    <Panel>
      <PanelHeader title="Mentorship" />
      <PanelBody className="pt-1">
        {app && (app.pictureUrl || app.title || app.bio || app.supervisor || app.phone) ? (
          <div className="flex gap-3 py-[12px] border-b border-line">
            {app.pictureUrl ? (
              <img src={app.pictureUrl} alt="" className="w-12 h-12 object-cover shrink-0 border border-line" />
            ) : null}
            <div className="min-w-0 text-small">
              {app.title ? <div className="font-semibold">{app.title}</div> : null}
              {app.supervisor ? <div className="text-ink-2">Supervisor: {app.supervisor}</div> : null}
              {app.phone ? <div className="text-ink-2">Phone: {app.phone}</div> : null}
              {app.bio ? <p className="mt-1 whitespace-pre-line">{app.bio}</p> : null}
              <div className="text-small text-ink-2 mt-1">From the mentorship app</div>
            </div>
          </div>
        ) : null}

        {seasons.map((s) => {
          const partners = (detail.pairings ?? []).filter((p) => p.seasonId === s.seasonId && p.role === s.role);
          const sessions = (detail.sessions ?? []).filter((x) => x.seasonId === s.seasonId && x.role === s.role);
          return (
            <div key={`${s.seasonId}-${s.role}`} className="py-[12px] border-b border-line last:border-b-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-table font-semibold">
                  {s.seasonName} · {s.role === 'mentor' ? 'Mentor' : 'Mentee'}
                </div>
                {!s.isActive ? (
                  <Pill tone="inactive">Removed before matching</Pill>
                ) : s.seasonIsActive ? (
                  <Pill tone="active">Current</Pill>
                ) : null}
              </div>
              {partners.length ? (
                <div className="text-small mt-1">
                  {s.role === 'mentee' ? 'Mentor: ' : 'Mentees: '}
                  {partners.map((p, i) => (
                    <span key={p.with?.id ?? i}>
                      {i ? ', ' : ''}
                      <button type="button" className="underline" onClick={() => open(p.with)}>
                        {p.with?.name ?? 'Unknown'}
                      </button>
                    </span>
                  ))}
                </div>
              ) : s.isActive ? (
                <div className="text-small text-ink-2 mt-1">Not paired</div>
              ) : null}
              <div className="text-small text-ink-2 mt-1">
                {s.sessionsAttended} of {s.sessionsScheduled} sessions attended
              </div>
              {sessions.length ? (
                <table className="w-full mt-2">
                  <tbody>
                    {sessions.map((x, i) => {
                      const upcoming = x.scheduledAt && new Date(x.scheduledAt).getTime() > now;
                      return (
                        <tr key={i} className="border-t border-line text-small">
                          <td className="py-[6px] tabular text-ink-2 w-[88px]">
                            {x.scheduledAt ? dateShort(x.scheduledAt) : DASH}
                          </td>
                          <td className="py-[6px]">
                            <button type="button" className="underline" onClick={() => open(x.with)}>
                              {x.with?.name ?? 'Unknown'}
                            </button>
                          </td>
                          <td className="py-[6px] text-right">
                            {upcoming ? 'Upcoming' : x.attended ? 'Attended' : 'Not attended'}
                            {x.feedbackSubmitted ? ' · feedback given' : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : null}
            </div>
          );
        })}

        {detail.staleNotes && detail.staleNotes.length ? (
          <div className="text-small text-ink-2 pt-3 border-t border-line">
            The mentorship app has newer answers than the registration profile:{' '}
            {detail.staleNotes
              .map((n) => `${FIELD_LABEL[n.field] ?? n.field} "${n.app}" (profile: "${n.platform}")`)
              .join('; ')}
            .
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}
